import { useMemo, useState } from 'react';
import VotingPointsPopup from '../../Dialog/VotingPoints';
import styles from './VotingPoints.module.scss';
import { TabKey } from '../../Dialog/VotingPoints';
import { ProjectInfo, ProjectTime } from '@/types/home';
import clx from 'classnames';
import { WINNER_EMPTY_Q } from '@/constants';
import { useParams } from '@web3/base-hooks';
import { hybirdOpen, isMobile } from '@web3/base-utils';
import ListedTag from './ListedTag';
import { WAVE_NUM, Winner } from './types';
import Image from 'next/image';
import { useFm, useToast } from '@web3/base-hooks';
import { scTrack } from '@web3/base-tracking';
import { Event, Page } from '@/config/track-config';

interface Props {
  wave1Projects?: ProjectInfo[];
  wave2Projects?: ProjectInfo[];
  projectTimeList?: ProjectTime[];
}

const VotingPoints = ({
  wave1Projects,
  wave2Projects,
  projectTimeList,
}: Props) => {
  const [votingPointsVisible, setVotingPointsVisible] = useState(false);
  const { locale } = useParams();
  const fm = useFm();
  const { toastInfo } = useToast();

  const waveNum = useMemo(() => {
    const nowTime = Date.now();
    let num = WAVE_NUM.OTHERS;
    if (!projectTimeList) return num;
    if (
      nowTime > projectTimeList[1].start &&
      nowTime <= projectTimeList[1].end
    ) {
      num = WAVE_NUM.ONE;
    } else if (nowTime > projectTimeList[1].end) {
      num = WAVE_NUM.TWO;
    }
    return num;
  }, [projectTimeList]);

  const winners = useMemo(() => {
    let winners: (Winner | null)[] = [null, null, null];
    if (waveNum === WAVE_NUM.ONE) {
      winners = [
        ...wave1Projects.map((p) => ({ ...p, waveNum: WAVE_NUM.ONE })),
        ...winners,
      ].slice(0, 4);
    } else if (waveNum === WAVE_NUM.TWO) {
      winners = [
        ...wave1Projects.map((p) => ({ ...p, waveNum: WAVE_NUM.ONE })),
        ...wave2Projects.map((p) => ({ ...p, waveNum: WAVE_NUM.TWO })),
        ...winners,
      ].slice(0, 4);
    }
    return winners;
  }, [wave1Projects, wave2Projects, waveNum]);

  const handleClickProj = (winner: Winner) => {
    if (!winner?.coin) {
      toastInfo(fm('searchCoinComingSoon'));
      return;
    }
    const token = winner.coin;
    const deeplink = `bybitapp://open/home?tab=3&symbol=${token}USDT`;
    const webUrl = `${window.location.origin}/${locale}/trade/spot/${token}/USDT`;
    hybirdOpen(deeplink, webUrl);
  };

  const handleClick = () => {
    scTrack(Event.Web3UserClick, {
      page_name: Page.HomePage,
      button_name: 'WhatisProjectsVotingPoints',
    });
    setVotingPointsVisible(true);
  };
  const wave1Top1TgText = fm('projectLiveOnBybitSpot1', {
    projectName: winners[0]?.name,
  });
  const wave1Top3TgText = winners[1]
    ? winners[1]?.coin
      ? fm('projectLiveOnBybitSpot2', {
          projectName1: winners[1]?.name,
          projectName2: winners[2]?.name,
          projectName3: winners[3]?.name,
        })
      : fm('wave2top3desc')
    : '';
  return (
    <>
      <div className={styles.wrapper}>
        <p className={styles.title}>
          {fm('projectsRankBased')}{' '}
          <span className={styles.link} onClick={handleClick}>
            {fm('votingPoints')}
          </span>
        </p>
        <p>
          {waveNum === WAVE_NUM.OTHERS
            ? fm('wave1top1desc')
            : fm('wave2top3desc')}
        </p>
        {!!winners[0] && (
          <div className={styles.list}>
            {winners.map((winner, index) =>
              winner ? (
                <div
                  key={winner.code}
                  className={clx(styles.item, styles.winnerItem)}
                  onClick={() => handleClickProj(winner)}
                >
                  <Image
                    className={styles.img}
                    loader={({ src }) => src}
                    src={winner.icon}
                    width={46}
                    height={46}
                    alt=""
                  />
                  <span className="w-full truncate">{winner.name}</span>
                  {!isMobile && (
                    <ListedTag
                      tagText={index === 0 ? wave1Top1TgText : wave1Top3TgText}
                    />
                  )}
                </div>
              ) : (
                <div key={index} className={clx(styles.item, styles.emptyItem)}>
                  <Image
                    className={styles.img}
                    loader={({ src }) => src}
                    src={WINNER_EMPTY_Q}
                    width={46}
                    height={46}
                    alt=""
                  />
                  {fm('next')}
                </div>
              )
            )}
            {isMobile && (
              <ListedTag
                tagText={
                  <span>
                    {wave1Top1TgText}
                    <br />
                    {wave1Top3TgText}
                  </span>
                }
              />
            )}
          </div>
        )}
      </div>
      <VotingPointsPopup
        visible={votingPointsVisible}
        onClose={() => setVotingPointsVisible(false)}
        defaultTab={TabKey.USER}
      />
    </>
  );
};

export default VotingPoints;
